import React, {useContext, useEffect, useState} from "react";
import {Box,Dialog,DialogActions,DialogContent,DialogTitle,IconButton,InputAdornment,TextField,Alert,Typography} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {useTranslation} from "react-i18next";
import {PrimaryBigButton, SecondaryBigButton} from "../../../../components/buttons/mainButton";
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import {IconPosition} from "../../../../components/buttons/buttonProps";
import {TOperation} from "../../../../interfaces/operation";
import {InfoPoints, WalletSelector, WarningPoints} from "./pointsUtils";
import {StoreContext} from "../../../../common/struct/store";
import {AppContext} from "../../../../App";
import {TEmailTemplate} from "../../../../interfaces/email";
import {getEmailTemplates} from "../../../../services/AdminService";
import {RequestFilterOperator} from "../../../../common/methods/ApiService";


type AddPointsToSelectedParticipantsProps = {
  openDialog: boolean,
  sendMoney: (amount: number, wallet: string) => void,
  closeDialog: () => void,
  operation?: TOperation,
  selectedElements: number,
  countWarningCredit: number,
  countWarningDebit: number
}
export default function AddPointsToSelectedParticipants(props: AddPointsToSelectedParticipantsProps): JSX.Element {
  const {openDialog, sendMoney, closeDialog, operation, selectedElements, countWarningCredit, countWarningDebit} = props;
  const [store] = useContext<StoreContext>(AppContext);
  const [pointValue, setPointValue] = useState<number>(1);
  const [selectedWallet, setSelectedWallet] = useState<string|undefined>(store.global.company.defaultWallet ? store.global.company.defaultWallet['@id']: undefined);
  const [disabledConfirm, setDisabledConfirm] = useState<boolean>(false);
  const displayWalletSelector = store.global.company.wallets.length > 2;
  const {t} = useTranslation();

  const onConfirmMoney = (): void => {
    if (operation == null) {
      return;
    }

    closeDialog();
    sendMoney(store.global.company.euroPerPoint * pointValue, selectedWallet ? selectedWallet : store.global.company.defaultWallet ? store.global.company.defaultWallet['@id']: 'none');
  }

  const selectedWalletObj = store.global.company.wallets.find((w) => w['@id'] === selectedWallet);
  const total = pointValue * store.global.company.euroPerPoint * selectedElements;
  const remaining = selectedWalletObj
    ? (selectedWalletObj.balance ?? 0) - total
    : 0;
  const isNegative = remaining < 0;

  useEffect(() => {
    if (total === 0) {
      setDisabledConfirm(true);
    } else {
      setDisabledConfirm(false);
    }
  }, [total]);

  const [templates, setTemplates] = useState<TEmailTemplate[]>([]);

  useEffect(() => {
    if (!operation?.id) return;
    getEmailTemplates(1, 100, "", {field: "updatedAt", sort: "desc"}, [
      {
        field: "operation",
        items: [
          {
            operator: RequestFilterOperator.EQUALS,
            value: `bo/operations/${operation.id}`,
            isDate: false,
            isDatePrecise: false
          }
        ]
      }
    ]).then((res) => {
      setTemplates(res.items);
    });
  }, [operation]);

  const hasNewPointTemplate = templates.some(
    (t) => t.newPointAdded === true
  );

  return operation == null ? <></> :
    <Dialog open={openDialog} onClose={closeDialog} sx={{display: "flex", justifyContent: "center"}}>
      <DialogTitle>
        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
          <Typography color="neutral.dark" sx={{fontWeight: "bold"}}
            variant="h2">{t('participants.give_money_to_selected_participants.title')}</Typography>
          <IconButton onClick={closeDialog}><CloseIcon/></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <form onSubmit={onConfirmMoney} >
          <Typography color="neutral.dark" sx={{fontWeight: "bold", mb: 2}}
            variant="body1">{t('participants.give_money_to_selected_participants.sub_title')}</Typography>
          <TextField onChange={(event: any): void => !isNaN(event.target.value)?setPointValue(Math.abs(event.target.value)):setPointValue(0)} type="string" color="info"
            value={pointValue}
            sx={{width: '100%', mr: 2, "& input": {p: 3}}}
            InputProps={{
              lang: "fr-FR",
              endAdornment: <InputAdornment position="end">
                <Typography color="neutral.dark" variant="body2">{t('participants.give_money_to_selected_participants.unit')}</Typography>
              </InputAdornment>,
              inputProps: {
                min: 1,
              }
            }}
          />
          {displayWalletSelector &&
            <WalletSelector selectedWallet={selectedWallet} setSelectedWallet={setSelectedWallet}/>
          }
        </form>
        <InfoPoints label="give_money_to_selected_participants" euroValue={store.global.company.euroPerPoint * pointValue} selectedElements={selectedElements}/>
        {countWarningCredit > 0 && countWarningDebit > 0 && <WarningPoints label="warning_participant" count1={countWarningCredit} count2={countWarningDebit}/>}
        {countWarningCredit > 0 && countWarningDebit == 0 && <WarningPoints label="warning_credit_participant" count1={countWarningCredit}/>}
        {countWarningDebit > 0 && countWarningCredit == 0 && <WarningPoints label="warning_debit_participant" count1={countWarningDebit}/>}
      </DialogContent>

      <DialogActions sx={{display: 'flex', justifyContent: 'right', borderTop: "1px solid", borderColor: "ornament.dark"}}>
        <Box sx={{width: "100%"}}>
          {isNegative && hasNewPointTemplate && (
            <Alert severity="warning" sx={{mt: 2}}>
              <Typography>
                {t('participants.give_money_to_selected_participants.warning_debit_participant_points')}
              </Typography>
            </Alert>
          )}
          <Box sx={{display: "flex", gap: 2, width: "100%", mt: 2}}>
            <SecondaryBigButton
              label={t('participants.give_money_to_selected_participants.cancel_button')}
              action={closeDialog} sx={{flex:1}}
            />
            <PrimaryBigButton
              label={t('participants.give_money_to_selected_participants.confirm_button')}
              icon={AddOutlinedIcon}
              position={IconPosition.LEFT}
              action={onConfirmMoney}
              disabled={disabledConfirm}
              sx={{flex:1}}
            />
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
}
